import {ReactElement, useRef} from 'react';
import {useEffect} from 'react';
import {useMemo} from 'react';
import React from 'react';
import {GroupItemsContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/group-items-contextual-options';
import {SpellcheckContextualItems} from '@Components/poster-editor/components/poster-editing-side-panel/components/spellcheck-contextual-items';
import {DrawEditingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-editing-options';
import {AudioPlaylistEditingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/audio-playlist-editing-options';
import {PosterEditingDefaultOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-default-options';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {PosterEditingVectorOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-vector-options';
import {PosterEditingQROptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-QR-options';
import {PosterEditingImageOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-image-options';
import {PosterEditingTextOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-text-options';
import {PosterEditingFancyTextOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-fancy-text-options';
import {PosterEditingVideoOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-video-options';
import {PosterEditingTableOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-table-options';
import {PosterEditingMenuOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-menu-options';
import {PosterEditingSlideshowOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-slideshow-options';
import {PosterEditingTabsOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-tabs-options';
import {PosterEditingTranscriptOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-transcript-options/poster-editing-transcript-options';
import {PosterEditingRectangleOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-rectangle-options/poster-editing-rectangle-options';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {PosterEditingLineOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-line-options/poster-editing-line-options';
import {useGetNumberOfActiveItems} from '@Hooks/poster-editor/useGetNumberOfActiveItems';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {getIsSpellCheckEnabled} from '@/reducers/user-preferences.reducer';
import {contextualMenuContentKeyValues} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {resetStatesWithId} from '@Components/contextual-menu/contextual-menu.reducer';

export function ContextualOptions(): ReactElement {
  const numberOfActiveItems = useGetNumberOfActiveItems();
  const selectedItemId = useSingleActivePosterItemId();
  const selectedItemType = useRef<ITEM_TYPE>();
  const isDrawModeActive = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isDrawModeOn;
  });
  const isImageGeneratingFromDrawing = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isImageBeingGenerated;
  });
  const isAudioPlaylistSelected = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID !== '';
  });
  const dispatch = useAppDispatch();
  const isSpellCheckEnabled = useAppSelector(getIsSpellCheckEnabled);

  const isSpellCheckSettingsActivated = useAppSelector((state) => {
    return state.posterEditor.isSpellCheckSettingsActive;
  });

  useEffect((): void => {
    if (selectedItemId === null) {
      return;
    }

    const itemType = window.posterEditor?.whiteboard?.getItemForId(selectedItemId)?.gitype;

    if (itemType === null) {
      return;
    }

    if (selectedItemType.current === null) {
      selectedItemType.current = itemType;
      return;
    }

    if (itemType !== selectedItemType.current) {
      dispatch(
        resetStatesWithId({
          ids: contextualMenuContentKeyValues,
        })
      );
    }
    selectedItemType.current = itemType;
  }, [selectedItemId]);

  const getContextualOptions = (): ReactElement => {
    if (numberOfActiveItems > 1) {
      return <GroupItemsContextualOptions key="group-item-contextual-options" />;
    }

    if (isSpellCheckSettingsActivated && isSpellCheckEnabled) {
      return <SpellcheckContextualItems key="spellcheck-contextual-options" />;
    }

    if (isDrawModeActive || isImageGeneratingFromDrawing) {
      return <DrawEditingOptions key="draw-contextual-options" />;
    }

    if (isAudioPlaylistSelected) {
      return <AudioPlaylistEditingOptions key="audio-contextual-options" />;
    }

    if (numberOfActiveItems === 0) {
      return <PosterEditingDefaultOptions key="default-contextual-options" />;
    }

    const selectedItemId = window.posterEditor?.whiteboard?.getSelectedItems()[0].uid;

    if (numberOfActiveItems === 1 && selectedItemId) {
      const activeItem = window.posterEditor?.whiteboard?.getItemForId(selectedItemId);
      switch (activeItem?.gitype) {
        case ITEM_TYPE.VECTOR:
          return <PosterEditingVectorOptions key="vector-contextual-options" />;
        case ITEM_TYPE.RECTANGLE:
          return <PosterEditingRectangleOptions key="rectangle-contextual-options" />;
        case ITEM_TYPE.QR_CODE:
          return <PosterEditingQROptions key="qrcode-contextual-options" />;
        case ITEM_TYPE.IMAGE:
          return <PosterEditingImageOptions key="image-contextual-options" />;
        case ITEM_TYPE.STICKER:
          return <PosterEditingImageOptions key="sticker-contextual-options" />;
        case ITEM_TYPE.TEXT:
          return <PosterEditingTextOptions key="text-contextual-options" />;
        case ITEM_TYPE.FANCY_TEXT:
          return <PosterEditingFancyTextOptions key="fancy-text-contextual-options" />;
        case ITEM_TYPE.VIDEO:
          return <PosterEditingVideoOptions key="video-contextual-options" />;
        case ITEM_TYPE.TABLE:
          return <PosterEditingTableOptions key="table-contextual-options" />;
        case ITEM_TYPE.MENU:
          return <PosterEditingMenuOptions key="menu-contextual-options" />;
        case ITEM_TYPE.SLIDESHOW:
          return <PosterEditingSlideshowOptions key="slideshow-contextual-options" />;
        case ITEM_TYPE.TAB:
          return <PosterEditingTabsOptions key="tab-contextual-options" />;
        case ITEM_TYPE.TRANSCRIPT:
          return <PosterEditingTranscriptOptions key="transcript-contextual-options" />;
        case ITEM_TYPE.LINE:
          return <PosterEditingLineOptions key="line-contextual-options" />;
        default:
          throw new Error(`unhandled item gitype: ${activeItem?.gitype}`);
      }
    }
    return <PosterEditingDefaultOptions key="default-contextual-options" />;
  };

  return useMemo((): ReactElement => {
    return getContextualOptions();
  }, [numberOfActiveItems, selectedItemId, isDrawModeActive, isImageGeneratingFromDrawing, isSpellCheckEnabled, isSpellCheckSettingsActivated, isAudioPlaylistSelected]);
}
